import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import countriesCitiesData from '../lib/countries-cities.json';
import { slugify } from '../lib/slug';
import SEO from '../components/SEO';

export default function Cities() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { countrySlug, citySlug } = useParams();
  const [countryName, setCountryName] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCity, setSelectedCity] = useState(null);
  const [lightboxImage, setLightboxImage] = useState('');
  const heroRef = useRef(null);

  // Read from SEO-friendly slug first, then query `country`
  useEffect(() => {
    if (countrySlug) {
      setCountryName(countrySlug.replace(/-/g, ' '));
      return;
    }
    const params = new URLSearchParams(location.search || '');
    const name = params.get('country');
    if (name) setCountryName(name);
  }, [location.search, countrySlug]);

  const country = useMemo(() => {
    const list = Array.isArray(countriesCitiesData?.countries) ? countriesCitiesData.countries : [];
    if (!countryName) return list[0] || null;
    return list.find((c) => String(c?.name || '').toLowerCase() === String(countryName).toLowerCase()) || list[0] || null;
  }, [countryName]);

  const cities = useMemo(() => {
    const arr = Array.isArray(country?.cities) ? country.cities : [];
    return arr;
  }, [country]);

  // Select city by slug if provided; else capital; else first
  useEffect(() => {
    const capitalName = country?.capital;
    const arr = Array.isArray(country?.cities) ? country.cities : [];
    let defaultCity = null;
    if (citySlug) {
      defaultCity = arr.find((c) => slugify(c?.name) === citySlug) || null;
    }
    if (capitalName) {
      defaultCity = defaultCity || arr.find((c) => String(c?.name || '').toLowerCase() === String(capitalName).toLowerCase()) || null;
    }
    if (!defaultCity) defaultCity = arr[0] || null;
    setSelectedCity(defaultCity || null);

    // Ensure URL includes city slug (default to capital) for canonical pattern
    if (!citySlug && defaultCity && country) {
      navigate(`/country/${slugify(country.name)}/cities/${slugify(defaultCity.name)}`, { replace: true });
    }
  }, [country, citySlug, navigate]);

  const filteredCities = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const arr = Array.isArray(cities) ? cities : [];
    if (!q) return arr;
    return arr.filter((c) => String(c?.name || '').toLowerCase().includes(q));
  }, [cities, search]);

  const countrySlugKey = slugify(country?.name || '');
  const currentCitySlugKey = slugify(selectedCity?.name || '');
  const cityNameT = selectedCity ? t(`city.${countrySlugKey}.${currentCitySlugKey}.name`, { defaultValue: selectedCity.name }) : '';
  const countryNameT = country ? t(`country.${countrySlugKey}.name`, { defaultValue: country?.name || '' }) : '';
  const cityDescT = selectedCity ? t(`city.${countrySlugKey}.${currentCitySlugKey}.description`, { defaultValue: selectedCity.description || '' }) : '';
  const seoTitle = selectedCity && country ? `${cityNameT}, ${countryNameT} – ${t('cities.seoTitleSuffix', { defaultValue: 'Creative Culture' })}` : `${t('cities.header', { defaultValue: 'Cities in ' })}${countryNameT || ''}`;
  const seoDescription = selectedCity && country ? `${cityNameT} in ${countryNameT}. ${cityDescT || ''}` : t('cities.subtitle', { defaultValue: 'Discover vibrant cities and their creative culture.' });

  return (
    <section className="container mx-auto px-4 py-8 space-y-8">
      <SEO title={seoTitle} description={seoDescription} />
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-3xl md:text-4xl font-bold text-primary-text">{t('cities.header', { defaultValue: 'Cities in ' })}{t(`country.${countrySlugKey}.name`, { defaultValue: country?.name || '' })}</h1>
          <div className="flex items-center gap-2">
            <label className="text-sm text-secondary-text">{t('cities.selectCountry', { defaultValue: 'Country' })}</label>
            <select
              value={country?.name || ''}
              onChange={(e) => {
                const name = e.target.value;
                setCountryName(name);
                const selected = (countriesCitiesData?.countries || []).find((c) => String(c?.name || '').toLowerCase() === String(name).toLowerCase());
                const cap = selected?.capital || (Array.isArray(selected?.cities) ? selected.cities[0]?.name : '');
                if (selected) {
                  navigate(`/country/${slugify(selected.name)}/cities/${slugify(cap || '')}`);
                }
              }}
              className="h-10 rounded-md bg-input-bg border border-card-border px-3 text-sm text-primary-text shadow-sm"
            >
              {(countriesCitiesData?.countries || []).map((c) => (
                <option key={c.code || c.name} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-base md:text-lg text-secondary-text">{t('cities.subtitle', { defaultValue: 'Discover vibrant cities and their creative culture.' })}</p>
      </div>

      {/* Hero: two columns - left image+description, right details */}
      {Array.isArray(cities) && cities.length > 0 && selectedCity && (
        <section ref={heroRef} className="rounded-xl overflow-hidden border border-card-border bg-card-bg">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left: image with overlay and description */}
            <div className="relative min-h-[320px] md:min-h-[380px]">
              <img
                src={resolveCityImage(selectedCity) || cityPlaceholder(selectedCity.name)}
                alt={`Featured: ${selectedCity.name}`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 hover:scale-105 cursor-zoom-in"
                onClick={() => setLightboxImage(resolveCityImage(selectedCity) || cityPlaceholder(selectedCity.name))}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/0" />
              <div className="absolute bottom-4 left-5 right-5">
                <div className="text-white font-semibold text-2xl drop-shadow">
                  {t(`city.${countrySlugKey}.${currentCitySlugKey}.name`, { defaultValue: selectedCity.name })}
                </div>
                <div className="text-white/90 text-sm drop-shadow">
                  {expandedCityDescription(t, selectedCity, country)}
                </div>
              </div>
            </div>
            {/* Right: details */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs text-secondary-text">{t('cities.country', { defaultValue: 'Country' })}</div>
                  <div className="text-lg font-semibold text-primary-text">{t(`country.${countrySlugKey}.name`, { defaultValue: country?.name || '' })}</div>
                </div>
                {/* Removed SEO-friendly URL link per request */}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-secondary-text">{t('city.language', { defaultValue: 'Local language' })}</div>
                  <div className="text-sm font-medium text-primary-text">{t(`country.${countrySlugKey}.languageName`, { defaultValue: languageForCountry(country?.name) })}</div>
                </div>
                <div>
                  <div className="text-xs text-secondary-text">{t('city.currency', { defaultValue: 'Currency' })}</div>
                  <div className="text-sm font-medium text-primary-text">{t(`country.${countrySlugKey}.currencyName`, { defaultValue: currencyForCountry(country?.name) })}</div>
                </div>
                <div>
                  <div className="text-xs text-secondary-text">{t('city.region', { defaultValue: 'Region' })}</div>
                  <div className="text-sm font-medium text-primary-text">{t(`country.${countrySlugKey}.regionName`, { defaultValue: regionForCountry(country?.name) })}</div>
                </div>
                <div>
                  <div className="text-xs text-secondary-text">{t('city.bestSeason', { defaultValue: 'Best season' })}</div>
                  <div className="text-sm font-medium text-primary-text">{t(`country.${countrySlugKey}.bestSeasonText`, { defaultValue: bestSeasonForCountry(country?.name) })}</div>
                </div>
              </div>
              <div className="mt-6">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('cities.searchPlaceholder', { defaultValue: 'Search cities' })}
                  className="w-full h-10 rounded-md bg-input-bg border border-card-border px-3 text-sm text-primary-text"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Cities list */}
      <section className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-primary-text">{t('cities.listHeader', { country: t(`country.${countrySlugKey}.name`, { defaultValue: country?.name || '' }), defaultValue: `Cities in ${country?.name || ''}` })}</h2>
          <div className="text-xs text-secondary-text">{t('cities.tip', { defaultValue: 'Click a city to view details' })}</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCities.map((c) => (
            <button
              key={c.id || c.name}
              onClick={() => {
                setSelectedCity(c);
                if (country?.name && c?.name) {
                  navigate(`/country/${slugify(country.name)}/cities/${slugify(c.name)}`);
                }
                heroRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              className={`city-card group rounded-xl overflow-hidden border border-card-border card-hover text-left focus:outline-none focus:ring-2 focus:ring-primary/40 ${selectedCity && selectedCity.name === c.name ? 'ring-2 ring-primary/50' : ''}`}
              aria-label={`Select ${c.name}`}
            >
              <div className="relative h-40">
                <img
                  src={resolveCityImage(c) || cityPlaceholder(c.name)}
                  alt={`${t(`city.${countrySlugKey}.${slugify(c.name)}.name`, { defaultValue: c.name })} image`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-black/0" />
                <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between">
                  <div className="text-white font-semibold text-lg drop-shadow">{t(`city.${countrySlugKey}.${slugify(c.name)}.name`, { defaultValue: c.name })}</div>
                  {country?.capital && country.capital === c.name && (
                    <span className="badge">{t('cities.capital', { defaultValue: 'Capital' })}</span>
                  )}
                </div>
              </div>
              <div className="p-4 city-card-content">
                <div className="text-sm text-secondary-text line-clamp-3">{expandedCityDescription(t, c, country)}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
          onClick={() => setLightboxImage('')}
        >
          <img src={lightboxImage} alt="City full view" className="max-h-[85vh] max-w-[90vw] rounded-lg shadow-2xl" />
        </div>
      )}
    </section>
  );
}

function expandedCityDescription(t, city, country) {
  const countrySlugKey = slugify(country?.name || '');
  const citySlugKey = slugify(city?.name || '');
  const base = String(
    t(`city.${countrySlugKey}.${citySlugKey}.description`, { defaultValue: city?.description || '' })
  ).trim();
  const name = t(`city.${countrySlugKey}.${citySlugKey}.name`, { defaultValue: city?.name || '' });
  const countryName = t(`country.${countrySlugKey}.name`, { defaultValue: country?.name || '' });
  const regionKey = regionKeyForCountry(country?.name || '');
  const languageKey = languageKeyForCountry(country?.name || '');
  const region = t(`region.${regionKey}`, { defaultValue: regionForCountry(country?.name || '') });
  const language = t(`language.${languageKey}`, { defaultValue: languageForCountry(country?.name || '') });
  const extra = t('city.extraTemplate', {
    defaultValue: `${name} is a key city in ${countryName}, part of ${region}. Locals primarily speak ${language}.`,
    name,
    country: countryName,
    region,
    language,
  });
  if (!base) return extra;
  return `${base} ${extra}`;
}
function languageKeyForCountry(name = '') {
  const n = String(name).toLowerCase();
  if (n.includes('sweden')) return 'swedish';
  if (n.includes('norway')) return 'norwegian';
  if (n.includes('denmark')) return 'danish';
  if (n.includes('finland')) return 'finnish_and_swedish';
  if (n.includes('iceland')) return 'icelandic';
  return 'local';
}
function languageForCountry(name = '') {
  const n = String(name).toLowerCase();
  if (n.includes('sweden')) return 'Swedish';
  if (n.includes('norway')) return 'Norwegian';
  if (n.includes('denmark')) return 'Danish';
  if (n.includes('finland')) return 'Finnish (and Swedish)';
  if (n.includes('iceland')) return 'Icelandic';
  return 'Local language';
}

function currencyForCountry(name = '') {
  const n = String(name).toLowerCase();
  if (n.includes('sweden')) return 'SEK (Swedish krona)';
  if (n.includes('norway')) return 'NOK (Norwegian krone)';
  if (n.includes('denmark')) return 'DKK (Danish krone)';
  if (n.includes('finland')) return 'EUR (Euro)';
  if (n.includes('iceland')) return 'ISK (Icelandic króna)';
  return 'Local currency';
}

function regionKeyForCountry(name = '') {
  const n = String(name).toLowerCase();
  if (n.includes('iceland')) return 'nordics';
  if (n.includes('finland')) return 'nordics';
  if (n.includes('sweden') || n.includes('norway') || n.includes('denmark')) return 'scandinavia';
  return 'europe';
}
function regionForCountry(name = '') {
  const n = String(name).toLowerCase();
  if (n.includes('iceland')) return 'Nordics';
  if (n.includes('finland')) return 'Nordics';
  if (n.includes('sweden') || n.includes('norway') || n.includes('denmark')) return 'Scandinavia';
  return 'Europe';
}

function bestSeasonForCountry(name = '') {
  const n = String(name).toLowerCase();
  if (n.includes('sweden') || n.includes('norway') || n.includes('finland')) return 'Summer (June–August)';
  if (n.includes('denmark')) return 'Late Spring to Early Autumn';
  if (n.includes('iceland')) return 'Summer for daylight, Winter for auroras';
  return 'Spring to Autumn';
}

// Unique, in-app placeholder for cities lacking dedicated images
function cityPlaceholder(name = '') {
  const text = (name || 'City').toUpperCase();
  const seed = [...text].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const hue = seed % 360;
  const svg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='800'>
      <defs>
        <linearGradient id='g' x1='0' x2='1' y1='0' y2='1'>
          <stop offset='0%' stop-color='hsl(${hue},70%,25%)' />
          <stop offset='100%' stop-color='hsl(${(hue+40)%360},70%,45%)' />
        </linearGradient>
      </defs>
      <rect width='100%' height='100%' fill='url(#g)' />
      <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
            font-family='system-ui, Segoe UI, Roboto' font-size='72' fill='rgba(255,255,255,0.92)'>${text}</text>
    </svg>`
  );
  return `data:image/svg+xml;charset=utf-8,${svg}`;
}

function resolveCityImage(city) {
  const src = String(city?.image || '');
  if (!src) return '';
  // Local asset
  if (src.startsWith('/')) return src;
  // In dev, avoid external-image ORB by showing placeholder
  if (import.meta?.env?.DEV && /^https?:\/\//i.test(src)) return '';
  return src;
}
